/**
 * The left panel — runners, their book position, and a 3+3 price ladder.
 *
 * Invariant I1 in practice: the marker is three layers deep, because a chip
 * alone is one screenshot away from being lost. The chip is in the card
 * actions, the word "simulated" is in the always-visible subtitle, and the
 * prices themselves never move — which the operator can verify by watching
 * them across a poll.
 */

import { Card, Chip, OddsLadder, Skeleton, StateBlock, type OddsLadderRow } from '@w7/ui'
import { Money } from '@/app/bits'
import { moneyCompact, odds } from '@/lib/format'
import type { BookPosition, MarketType, RunnerPrices } from '@/data/types'

const FANCY: MarketType[] = ['Fancy', 'Premium Fancy']

export function PriceLadderPanel({
  eventName,
  marketName,
  marketType,
  prices,
  positions,
  loading,
}: {
  eventName: string
  marketName: string
  marketType: MarketType | null
  prices: RunnerPrices[]
  /** The market's selections, in market order — NEVER re-sorted. */
  positions: BookPosition[]
  loading: boolean
}) {
  // Market order, always. The operator reads this list against the book table
  // beside it; a different order in one of the two is a trap, not a preference.
  const rows: OddsLadderRow[] = positions.map((position) => {
    const runner = prices.find((entry) => entry.selection === position.name)
    return {
      key: position.name,
      runner: (
        <span className="w7-mb-runner">
          <span className="w7-mb-runner-name w7-truncate" title={position.name}>
            {position.name}
          </span>
          <Money value={position.net} signed className="w7-mb-runner-net" />
        </span>
      ),
      // `back` arrives outermost-first and `lay` best-first, which is exactly
      // what OddsLadder wants — pass them straight through rather than
      // reversing here, or the two best prices stop meeting in the middle.
      back: (runner?.back ?? []).map((level) => ({
        price: odds(level.price),
        size: moneyCompact(level.size),
      })),
      lay: (runner?.lay ?? []).map((level) => ({
        price: odds(level.price),
        size: moneyCompact(level.size),
      })),
    }
  })

  return (
    <Card
      title="Runners & prices"
      actions={
        <Chip size="sm" tone="warn" icon="warning">
          Simulated prices
        </Chip>
      }
      subtitle="No exchange price feed is connected to this console. These prices are derived from the fixture's own probabilities so the ladder is realistic — they do not move, and they are not the market."
    >
      {loading ? (
        <div className="w7-col" style={{ gap: 'var(--w7-s2)' }} aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={44} radius={8} />
          ))}
        </div>
      ) : positions.length === 0 ? (
        <StateBlock icon="exposure" title="This market has no runners">
          The exchange returned a market with no selections on it. Nothing can be priced or
          positioned until it does.
        </StateBlock>
      ) : (
        <OddsLadder label={`${eventName} — ${marketName} prices`} rows={rows} />
      )}

      {marketType && FANCY.includes(marketType) ? (
        <p className="w7-mb-note">
          Fancy lines trade as a runs line and a size, not decimal odds. This ladder prices them as a
          two-way market — a simplification of the fixture, not of the exchange.
        </p>
      ) : null}
    </Card>
  )
}
